import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { PopoverModule, Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';

import { ModuleService, AppModule } from '@layout/service/module.service';
import { AuthService } from '@auth/services/auth.service';

@Component({
  selector: 'app-module-selector-mobile',
  standalone: true,
  imports: [CommonModule, PopoverModule, ButtonModule, RippleModule],
  template: `
    <div class="module-selector-mobile px-3 py-2 border-bottom-1 surface-border bg-card">
      <button
        pButton
        pRipple
        type="button"
        class="p-button-outlined p-button-secondary w-full flex justify-between items-center current-module-mobile-btn"
        (click)="op.toggle($event)"
      >
        <div class="flex items-center">
          <i [class]="activeModule()?.icon || 'pi pi-th-large'" class="mr-3 text-primary text-xl"></i>
          <span class="text-900 font-bold text-lg">{{ activeModule()?.label || 'Seleccionar Módulo' }}</span>
        </div>
        <i class="pi pi-chevron-down text-sm"></i>
      </button>

      <p-popover #op [style]="{ width: '90vw' }">
        <!-- El alto se limita y el contenido scrollea: con 14 módulos en dos
             columnas la rejilla mide más que la pantalla de un móvil y los
             últimos quedaban inalcanzables. -->
        <div class="p-3 module-popover-body">
          <div class="text-sm font-bold text-500 mb-4 uppercase tracking-wider px-2">Mis Módulos</div>
          <div class="grid grid-cols-2 gap-3">
            @for (module of visibleModules(); track module.id) {
              <button
                type="button"
                class="module-grid-item"
                [class.active]="moduleService.isModuleSelected(module.id)"
                (click)="onModuleClick(module, op)"
                pRipple
              >
                <div class="icon-box">
                  <i [class]="module.icon" style="font-size: 1.5rem"></i>
                </div>
                <span class="module-name">{{ module.label }}</span>
                @if (moduleService.isModuleSelected(module.id)) {
                  <div class="active-indicator"></div>
                }
              </button>
            }
          </div>
        </div>
      </p-popover>
    </div>
  `,
  styles: [
    `
      .module-selector-mobile {
        display: block;
        background: var(--surface-card);

        @media (min-width: 992px) {
          display: none;
        }
      }

      /* El scroll de .module-popover-body vive en src/assets/_overrides.scss:
         el contenido del popover cuelga de body y el CSS encapsulado no lo alcanza. */

      .current-module-mobile-btn {
        padding: 0.75rem 1rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-color: var(--surface-border);
        color: var(--text-color);
        border-radius: 10px;

        &:hover {
          background: var(--surface-hover);
        }
      }

      .module-grid-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1.25rem 0.75rem;
        border-radius: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        text-align: center;
        background: transparent;
        border: none;
        width: 100%;

        .icon-box {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          background: var(--surface-100);
          margin-bottom: 0.75rem;
          color: var(--text-color-secondary);
          font-size: 2.5rem; /* Iconos más grandes en móvil */
          transition: all 0.2s;
        }

        .module-name {
          font-size: 0.875rem;
          font-weight: 700; /* Texto más marcado en móvil */
          color: var(--text-color);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        &:active {
          transform: scale(0.95);
        }

        &.active {
          background: var(--primary-50);
          .icon-box {
            background: var(--primary-color);
            color: #ffffff;
            box-shadow: 0 4px 12px color-mix(in srgb, var(--primary-color) 40%, transparent);
          }
          .module-name {
            color: var(--primary-color);
            font-weight: 700;
          }
        }
      }

      .active-indicator {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 10px;
        height: 10px;
        background: var(--primary-color);
        border-radius: 50%;
        border: 2px solid var(--surface-card);
      }

      .bg-card {
        background: var(--surface-card);
      }
    `,
  ],
})
export class AppModuleSelectorMobileComponent {
  moduleService = inject(ModuleService);
  private authService = inject(AuthService);

  activeModule = computed(() =>
    this.moduleService.visibleModules().find((m) => this.moduleService.isModuleSelected(m.id))
  );

  isAdmin = computed(() => this.authService.isAdmin());

  visibleModules = computed(() => {
    return this.moduleService.visibleModules().filter((module) => {
      if (module.id === 'admin') return this.isAdmin();
      return module.visible !== false;
    });
  });

  onModuleClick(module: AppModule, op: Popover): void {
    this.moduleService.selectModule(module.id);
    op.hide();
  }
}
